import { Resume } from '@sr/types';
import { throttle } from 'lodash';
import React, { useCallback } from 'react';
import { Slider } from 'theme-ui';
import { Box, ExpandIcon } from '../../ui';
import API from '../../utils/api';

export default function SpacingControl({
  resume,
  setLocalBaseSize,
  localBaseSize,
}: {
  resume: Resume;
  setLocalBaseSize: (arg0: number) => void;
  localBaseSize: number;
}) {
  const { activeTemplateId } = resume.style;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const size: number = e.target.valueAsNumber;
    throttledSetLocalBaseSize(size);
    throttledSetServerBaseSize(size);
  }

  // A short throttle duration for fast/smooth preview rendering
  //eslint-disable-next-line
  const throttledSetLocalBaseSize = useCallback(
    throttle((size: number) => {
      setLocalBaseSize(size);
    }, 10),
    [resume.style.activeTemplateId]
  );

  // A longer throttle since this isn't used for the local preview
  //eslint-disable-next-line
  const throttledSetServerBaseSize = useCallback(
    throttle((size: number) => {
      API.resume.setProperty({
        path: `${resume.id}/style/customizations/${activeTemplateId}/baseSize`,
        value: size,
      });
      void API.events.logEvent({
        userId: resume.owner,
        eventName: 'Resume Spacing Changed',
        properties: {
          Template: resume.style.activeTemplateId,
          Size: size,
        },
      });
    }, 2000),
    [resume.style.activeTemplateId]
  );

  return (
    <Box mr={4} mt={4} mb={5}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ExpandIcon color="white" mr={3} />
          <Box sx={{ color: 'white', fontWeight: 'bold' }}>Spacing</Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          ml: 40,
          mt: 5,
        }}
      >
        <Slider
          type="range"
          min={1}
          max={30}
          step={0.5}
          value={localBaseSize}
          onChange={handleChange}
          data-testid="spacing-slider"
        />
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 12,
            width: 3,
            height: 3,
            borderRadius: 99,
            bg: 'white50',
            transform: 'translateX(-50%)',
          }}
        />
      </Box>
    </Box>
  );
}
